'use client'

import { useEffect, useId, useRef, useState } from 'react'
import Link from 'next/link'
import { GiveawayEntryResponseSchema } from '@mist/contracts/promotion'
import type { PublicConfig } from '@mist/contracts/api'
import { ApiError, postApi } from '@/lib/api-client'
import { getPublicConfig } from '@/lib/public-config'
import { readSubscriberHandoff } from '@/lib/session'
import { trackApiError, trackEvent } from '@/lib/analytics'
import TurnstileWidget from './TurnstileWidget'
import { CURRENT_RULES_VERSION } from '@/lib/site'

/**
 * MIST-0404 optional giveaway entry (R-EXP-04, R-EXP-05, R-EXP-07, R-COMP-03).
 * Offered only after email verification: it requires the session-bound CSRF
 * token from the verify handoff. Entry is free (no purchase), the referral URL
 * is never shown (email-only), and marketing consent stays separate from entry.
 * Ineligible countries keep their launch subscription.
 */

const COUNTRIES = [
  { code: '', label: 'Select your country' },
  { code: 'US', label: 'United States' },
  { code: 'GB', label: 'United Kingdom' },
  { code: 'AU', label: 'Australia' },
  { code: 'FR', label: 'France' },
  { code: 'CN', label: 'China' },
] as const

type Phase = 'gated' | 'ready' | 'submitting' | 'entered' | 'ineligible'
type ActiveRules = NonNullable<PublicConfig['activeRules']>
type GiveawayError = { field: 'country' | 'ageAttested' | 'rulesAccepted' | 'form'; message: string } | null

export default function GiveawayForm() {
  const countryId = useId()
  const ageId = useId()
  const rulesId = useId()
  const marketingId = useId()
  const countryErrorId = useId()
  const ageErrorId = useId()
  const rulesErrorId = useId()
  const formErrorId = useId()
  const rulesDescriptionId = useId()
  const [phase, setPhase] = useState<Phase>('gated')
  const [error, setError] = useState<GiveawayError>(null)
  const csrf = useRef<string | null>(null)
  const [activeRules, setActiveRules] = useState<ActiveRules | null>(null)
  const outcomeHeadingRef = useRef<HTMLHeadingElement>(null)
  const previousPhase = useRef<Phase>('gated')

  useEffect(() => {
    const handoff = readSubscriberHandoff()
    if (!handoff) return
    csrf.current = handoff.csrfToken
    trackEvent('mist_giveaway_started', { route: '/giveaway' })
    void getPublicConfig()
      .then((config) => {
        if (config.activeRules && config.giveawayAvailable) setActiveRules(config.activeRules)
      })
      .finally(() => setPhase('ready'))
  }, [])

  useEffect(() => {
    const movedToOutcome = previousPhase.current !== phase && (phase === 'entered' || phase === 'ineligible' || phase === 'gated')
    if (movedToOutcome) outcomeHeadingRef.current?.focus()
    previousPhase.current = phase
  }, [phase])

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError(null)
    const token = csrf.current
    if (!token) {
      setError({ field: 'form', message: 'Your verification session has expired. Please confirm your email again.' })
      setPhase('gated')
      return
    }
    const form = new FormData(event.currentTarget)
    const countryCode = String(form.get('countryCode') ?? '')
    const ageAttested = form.get('ageAttested') === 'true'
    const rulesAccepted = form.get('rulesAccepted') === 'true'
    const turnstileToken = String(form.get('cf-turnstile-response') ?? '')
    if (!countryCode) {
      setError({ field: 'country', message: 'Please choose your country.' })
      return
    }
    if (!ageAttested) {
      setError({ field: 'ageAttested', message: 'Confirm you are 18 years of age or older.' })
      return
    }
    if (!rulesAccepted) {
      setError({ field: 'rulesAccepted', message: 'Accept the Official Rules before entering.' })
      return
    }
    if (!activeRules) {
      setError({ field: 'form', message: 'Official Rules are not available for entry yet. Please try again later.' })
      return
    }
    setPhase('submitting')
    trackEvent('mist_eligibility_checked', { route: '/giveaway' })
    try {
      const result = await postApi(
        '/api/v1/giveaway/entry',
        GiveawayEntryResponseSchema,
        {
          countryCode,
          ageAttested,
          rulesVersion: activeRules.version,
          rulesHash: activeRules.rulesHash,
          giveawayConsent: true,
          marketingConsent: form.get('marketingConsent') === 'true',
          idempotencyKey: crypto.randomUUID(),
          // Injected into the form by Cloudflare's implicit rendering. Absent when
          // the widget did not load; the API grades that `missing` and throttles.
          ...(turnstileToken === '' ? {} : { turnstileToken }),
        },
        { csrfToken: token },
      )
      if (result.entered) {
        trackEvent('mist_giveaway_entered', { route: '/giveaway' })
        setPhase('entered')
      }
    } catch (caught) {
      if (!(caught instanceof ApiError)) throw caught
      trackApiError('/api/v1/giveaway/entry', caught)
      if (caught.status === 401) {
        setError({ field: 'form', message: 'Your verification session has expired. Please confirm your email again.' })
        setPhase('gated')
      } else if (caught.status === 403 && caught.code === 'COUNTRY_NOT_ALLOWED') {
        setPhase('ineligible')
      } else if (caught.code === 'RULES_MISMATCH' || caught.code === 'CONFIG_NOT_OPEN') {
        setError({ field: 'form', message: 'The Official Rules changed before entry. Please refresh and try again.' })
        setPhase('ready')
      } else {
        setError({ field: 'form', message: caught.message })
        setPhase('ready')
      }
    }
  }

  const countryError = error?.field === 'country' ? error.message : null
  const ageError = error?.field === 'ageAttested' ? error.message : null
  const rulesError = error?.field === 'rulesAccepted' ? error.message : null
  const formError = error?.field === 'form' ? error.message : null
  const statusMessage =
    phase === 'entered'
      ? 'Giveaway entry completed. Your private referral link is delivered by email only.'
      : phase === 'ineligible'
        ? 'Your launch place is kept. Your country is not currently eligible for giveaway entry.'
        : phase === 'submitting'
          ? 'Submitting your free giveaway entry.'
          : formError ?? ''

  if (phase === 'gated') {
    return (
      <div className="stack measure">
        <p className="visually-hidden" role="status" aria-live="polite" aria-atomic="true">
          {statusMessage}
        </p>
        <h2 ref={outcomeHeadingRef} tabIndex={formError ? -1 : undefined} className="eyebrow">
          One step first
        </h2>
        <p className="lede">Confirm your email to open the optional giveaway branch.</p>
        {formError ? (
          <p id={formErrorId} className="error" role="alert">
            {formError}
          </p>
        ) : null}
        <p>
          <Link className="mist-button mist-button--ghost" href="/verify">
            Go to verification
          </Link>
        </p>
      </div>
    )
  }

  if (phase === 'entered') {
    return (
      <div className="stack measure">
        <p className="visually-hidden" role="status" aria-live="polite" aria-atomic="true">
          {statusMessage}
        </p>
        <h2 ref={outcomeHeadingRef} tabIndex={-1} className="eyebrow">
          You're entered
        </h2>
        <p className="lede">Your free base entry is in. We've emailed your private referral link — it is delivered by email only.</p>
        <p className="hint">Each eligible, unique, verified referral adds one entry. No purchase is ever required.</p>
      </div>
    )
  }

  if (phase === 'ineligible') {
    return (
      <div className="stack measure">
        <p className="visually-hidden" role="status" aria-live="polite" aria-atomic="true">
          {statusMessage}
        </p>
        <h2 ref={outcomeHeadingRef} tabIndex={-1} className="eyebrow">
          Launch place kept
        </h2>
        <p className="lede">
          Your country is not currently eligible to enter, so no entry or referral credit was created. Your launch subscription remains
          active and unaffected.
        </p>
      </div>
    )
  }

  return (
    <div className="stack" style={{ maxWidth: '32rem' }}>
      <p className="visually-hidden" role="status" aria-live="polite" aria-atomic="true">
        {statusMessage}
      </p>
      <form onSubmit={onSubmit} className="stack" noValidate>
        <div className="field">
          <label htmlFor={countryId}>Country</label>
          <select
            id={countryId}
            name="countryCode"
            defaultValue=""
            required
            aria-invalid={countryError ? 'true' : undefined}
            aria-describedby={countryError ? countryErrorId : undefined}
          >
            {COUNTRIES.map((country) => (
              <option key={country.code} value={country.code} disabled={country.code === ''}>
                {country.label}
              </option>
            ))}
          </select>
          {countryError ? (
            <p id={countryErrorId} className="error" role="alert">
              {countryError}
            </p>
          ) : null}
        </div>
        <label className="consent" htmlFor={ageId}>
          <input
            id={ageId}
            type="checkbox"
            name="ageAttested"
            value="true"
            required
            aria-invalid={ageError ? 'true' : undefined}
            aria-describedby={ageError ? ageErrorId : undefined}
          />
          <span>I confirm I am 18 years of age or older.</span>
        </label>
        {ageError ? (
          <p id={ageErrorId} className="error" role="alert">
            {ageError}
          </p>
        ) : null}
        <label className="consent" htmlFor={rulesId}>
          <input
            id={rulesId}
            type="checkbox"
            name="rulesAccepted"
            value="true"
            required
            aria-invalid={rulesError ? 'true' : undefined}
            aria-describedby={rulesError ? `${rulesDescriptionId} ${rulesErrorId}` : rulesDescriptionId}
          />
          <span>I accept the Official Rules.</span>
        </label>
        <p id={rulesDescriptionId} className="hint">
          Read the <Link href={`/official-rules/${activeRules?.version ?? CURRENT_RULES_VERSION}`}>Official Rules</Link> before entering.
        </p>
        {rulesError ? (
          <p id={rulesErrorId} className="error" role="alert">
            {rulesError}
          </p>
        ) : null}
        <label className="consent" htmlFor={marketingId}>
          <input id={marketingId} type="checkbox" name="marketingConsent" value="true" />
          <span>Optionally, send me MIST by BDQ news. Separate from entry and never required.</span>
        </label>
        {formError ? (
          <p id={formErrorId} className="error" role="alert">
            {formError}
          </p>
        ) : null}
        <TurnstileWidget action="giveaway_entry" />
        <button className="mist-button mist-button--primary" type="submit" disabled={phase === 'submitting'}>
          {phase === 'submitting' ? 'Entering…' : 'Enter — free, no purchase'}
        </button>
      </form>
    </div>
  )
}
